import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, TemplateRef, ViewChild, inject, output, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import Swal from 'sweetalert2';
import { NumberInputComponent } from '../../shared/components/number-input/number-input.component';
import { TrainingProviderAddAttendeeStatusEnum } from '../../shared/enums/add-attendee-status';
import { InvoiceStatusEnum } from '../../shared/enums/invoice-status.enum';
import { SmartLabelEnum } from '../../shared/enums/smartlabel.enum';
import { AttendeePreview } from '../../shared/models/attendee-preview.model';
import { AttendeeType } from '../../shared/models/attendee-type.model';
import { Attendee } from '../../shared/models/attendee.model';
import { Event } from '../../shared/models/event.model';
import { PageResult } from '../../shared/models/page-result.model';
import { RegistryApiService } from '../../shared/services/registry-api.service';
import { SmartLabelService } from '../../shared/services/smartlabel.service';
import { AttendanceAddByScannerComponent } from '../attendance-add-by-scanner/attendance-add-by-scanner.component';
import { AttendanceAddModalComponent } from '../attendance-add-modal/attendance-add-modal.component';
import { AttendanceForm } from '../attendance-form/attendance-form.form';

@Component({
    selector: 'attendance-add',
    standalone: true,
    providers: [RegistryApiService, BsModalService],
    imports: [
        CommonModule,
        FormsModule,
        AttendanceAddModalComponent,
        NumberInputComponent,
        AttendanceAddByScannerComponent,
    ],
    templateUrl: './attendance-add.component.html',
})
export class AttendanceAddComponent implements OnInit {
    /** TODO: Unexpected any. Specify a different type. */
    @ViewChild('addAttendeeModalRef')
    public addAttendeeModalRef!: TemplateRef<any>;
    @ViewChild('scanner') public scanner:
        | AttendanceAddByScannerComponent
        | undefined;

    @Input({ required: true }) public event!: Event;

    @Input({ required: true }) public isOnline: boolean = true;

    @Input({ required: true }) public attendanceForm!: AttendanceForm;

    private readonly destroyRef = inject(DestroyRef);
    private readonly smartLabelService = inject(SmartLabelService);

    public refreshList = output<PageResult<Attendee>>();

    public verifyAttendee = output<Attendee>();

    public attendeeId: FormControl<number | null> = new FormControl<
        number | null
    >(null);

    public hasClickedSearch: boolean = false;

    public isConfirmingAttendee: boolean = false;

    public showAddAttendeeModal: boolean = false;

    public modalLoaded: boolean = false;

    public showAddComponent: boolean = false;

    public attendeeTypes: AttendeeType[] = [];

    public attendee: Attendee = new Attendee();

    public registryLabel: string = 'N/A';

    private currentModal?: BsModalRef;

    private readonly modalConfig = {
        animated: true,
        backdrop: 'static' as const,
        keyboard: false,
    };

    private readonly smartLabels = {
        [SmartLabelEnum.RegistryIDLabel]: '',
    };

    constructor(
        private readonly registryApiService: RegistryApiService,
        private readonly modalService: BsModalService,
    ) { }

    protected get inputPresent(): boolean {
        return !!this.attendeeId || this.attendeeId == 0;
    }

    protected get showAddAttendeeErrorLabel(): boolean {
        return !this.inputPresent && this.hasClickedSearch;
    }

    protected get isPaidEvent(): boolean {
        if (this.event?.freeEvent) {
            return !this.event.freeEvent && this.event?.eventFee == null;
        }
        return false;
    }

    protected get disableAddAttendee(): boolean {
        return this.hasClickedSearch || !this.attendeeId.value;
    }

    private readonly paymentStatusText = (attendee: Attendee): string => {
        let paymentStatus: boolean | undefined;
        if (attendee?.registrationInvoiceId !== null) {
            paymentStatus =
                attendee?.registrationInvoiceStatusId ===
                InvoiceStatusEnum.Paid;
        }
        if (paymentStatus === undefined) {
            return '';
        } else if (paymentStatus) {
            return '<br>Payment Status: Paid';
        } else {
            return '<br>Payment Status: Not Paid';
        }
    };

    /** TODO: Unexpected any. Specify a different type. */
    private readonly openModal = (modalTemplate: TemplateRef<any>): void => {
        this.currentModal = this.modalService.show(
            modalTemplate,
            this.modalConfig,
        );
    };

    protected readonly exitModal = (): void => {
        this.currentModal?.hide();
        this.hasClickedSearch = false;
        this.attendeeId.reset();
    };

    protected readonly showScanner = (): void => {
        this.scanner?.showSlider();
    };

    protected readonly onScanComplete = async (result: string): Promise<void> => {
        const attendeePreview: AttendeePreview =
            await this.registryApiService.isValidQRCode(this.event.id, result);
        this.hasClickedSearch = true;
        this.handlePreviewResponse(attendeePreview, this.addAttendeeModalRef);
    };

    protected readonly toggleAddAttendeeComponent = (): void => {
        this.showAddComponent = !this.showAddComponent;
    };

    protected readonly confirmAttendee = async (
        attendeeTypeId: number | undefined,
    ): Promise<void> => {
        if (this.isConfirmingAttendee) return;

        this.isConfirmingAttendee = true;
        try {
            const attendeeList: PageResult<Attendee> =
                await this.registryApiService.addAttendee(
                    this.event?.id,
                    this.attendeeId.value!,
                    attendeeTypeId,
                    this.attendanceForm.currentPage,
                    this.attendanceForm.pageSize,
                );
            this.refreshList.emit(attendeeList);
        } catch {
            Swal.fire({
                title: 'Error',
                text: 'An error occurred while adding the attendee.',
                icon: 'error',
                confirmButtonText: 'Ok',
            });
        }
        this.hasClickedSearch = false;
        this.isConfirmingAttendee = false;
    };

    /** TODO: Unexpected any. Specify a different type. */
    protected readonly addAttendee = async (
        modalTemplate: TemplateRef<any>,
    ): Promise<void> => {
        if (this.disableAddAttendee) return;

        this.hasClickedSearch = true;
        if (this.inputPresent && !!this.event?.id) {
            const response = await this.registryApiService.getAttendeePreview(
                this.event?.id,
                this.attendeeId.value!,
            );
            this.handlePreviewResponse(response, modalTemplate);
        }
    };

    /** TODO: Unexpected any. Specify a different type. */
    private readonly handlePreviewResponse = async (
        response: AttendeePreview,
        modalTemplate: TemplateRef<any>,
    ): Promise<void> => {
        switch (response.status) {
            case TrainingProviderAddAttendeeStatusEnum.Success:
                this.attendee = response.attendee;
                this.attendeeTypes = await this.registryApiService.getAttendeeTypes();
                this.registryLabel = this.smartLabels[SmartLabelEnum.RegistryIDLabel];
                this.attendeeId.setValue(this.attendee.appId);
                this.openModal(modalTemplate);
                break;

            case TrainingProviderAddAttendeeStatusEnum.Unauthorized:
            case TrainingProviderAddAttendeeStatusEnum.UserIsLocked:
            case TrainingProviderAddAttendeeStatusEnum.UserDoesNotExist:
                Swal.fire({
                    title: 'No accounts were found.',
                    text: 'Modify your search and try again.',
                    icon: 'error',
                    confirmButtonText: 'Ok',
                });
                this.hasClickedSearch = false;
                break;

            case TrainingProviderAddAttendeeStatusEnum.InvalidUserFromScan:
                Swal.fire({
                    title: 'No account was found.',
                    text: 'Valid account was not found.',
                    icon: 'error',
                    confirmButtonText: 'Ok',
                });
                this.hasClickedSearch = false;
                break;

            case TrainingProviderAddAttendeeStatusEnum.AttendeeAlreadyExists: {
                const label: string =
                    this.smartLabels[SmartLabelEnum.RegistryIDLabel];
                    
                Swal.fire({
                    title: 'Already on Roster',
                    icon: 'error',
                    html: `The following attendee is already a verified attendee
                    <br/>${label} #${response.attendee.appId || 'N/A'}
                    <br/>Name: ${response.attendee.firstName + ' ' + response.attendee.lastName}
                    <br/>Email: ${response.attendee.email || 'N/A'}
                    ${this.paymentStatusText(response.attendee)}`,
                    confirmButtonText: 'Ok',
                });
                this.hasClickedSearch = false;
                break;
            }

            case TrainingProviderAddAttendeeStatusEnum.AttendeeAlreadyExistsAndIsNotVerified:
                this.verifyAttendee.emit(response.attendee);
                this.hasClickedSearch = false;
                break;

            case TrainingProviderAddAttendeeStatusEnum.InvalidQRCode:
                this.scanner?.showSlider();
                Swal.fire({
                    title: 'Invalid QR code',
                    text: 'The QR code is invalid',
                    icon: 'error',
                    confirmButtonText: 'Ok',
                });
                this.hasClickedSearch = false;
                break;

            default:
                // this will occur if there is a status that is not handled, this should not happen
                Swal.fire({
                    title: 'Error',
                    text: 'An error occurred while adding the attendee.',
                    icon: 'error',
                    confirmButtonText: 'Ok',
                });
                this.hasClickedSearch = false;
                break;
        }
    };

    /** Initializes smartlabel values with fallbacks. */
    private readonly initSmartLabels = (): void => {
        /** Re-type the entries so TypeScript treats each key as a valid smartlabels key. */
        const smartLabelEntries = Object.entries(this.smartLabels) as Array<
            [keyof typeof this.smartLabels, string]
        >;

        /** Load each smartlabel from the service and fall back to its default text when needed. */
        for (const [labelName, defaultText] of smartLabelEntries) {
            this.smartLabelService
                .getLabelText(labelName, { fallback: defaultText })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(
                    (translatedText: string) =>
                        (this.smartLabels[labelName] = translatedText),
                );
        }
    };

    /** Performs synchronous initialization logic. */
    private readonly onInitSync = (): void => {
        this.initSmartLabels();
    };

    /** Performs asynchronous initialization logic. */
    private readonly onInitAsync = async (): Promise<void> => {
        // Optional asynchronous initialization logic can be implemented in derived classes.
    };

    // Initialize the component
    ngOnInit(): void {
        this.onInitSync();
        this.onInitAsync().finally(() => {
            /** Filler for when the async calls are done. */
        });
    }
}
